import React, { useState } from "react";
import {
  TrendingUp,
  Users,
  CheckCircle,
  ArrowRight,
  Target,
  Lightbulb,
  Rocket,
  Shield,
  Crown,
  BarChart3,
  Heart,
  Zap,
} from "lucide-react";

interface AuthorityResult {
  score: number;
  level: string;
  color: string;
  bgColor: string;
  iconColor: string;
  description: string;
  visibilityScore: number;
  trustScore: number;
  potential: string;
  icon: React.ReactNode;
  recommendations: {
    visibility: string[];
    trust: string[];
    quickWins: string[];
    longTerm: string[];
  };
  nextLevel: {
    name: string;
    requirements: string[];
  };
}

const AuthorityQuotientResult: React.FC = () => {
  const [selectedResult, setSelectedResult] = useState<string>("starting");
  const [leadData, setLeadData] = useState({ name: "", email: "" });
  const [submitted, setSubmitted] = useState(false);

  const authorityResults: Record<string, AuthorityResult> = {
    starting: {
      score: 15,
      level: "Starting Journey",
      color: "text-red-600",
      bgColor: "bg-red-50",
      iconColor: "text-red-500",
      description:
        "You're at the beginning of your authority journey. Every expert started here!",
      visibilityScore: 8,
      trustScore: 7,
      potential:
        "Unlimited potential ahead! You have a blank canvas to build massive authority.",
      icon: <Rocket className="w-10 h-10" />,
      recommendations: {
        visibility: [
          "Create valuable content consistently (3-5 posts per week)",
          "Join relevant Facebook groups and provide helpful answers",
          "Start an email newsletter with a compelling lead magnet",
          "Collaborate with others in your industry",
          "Use relevant hashtags to increase discoverability",
        ],
        trust: [
          "Share your personal story and journey",
          "Provide free value before asking for anything",
          "Respond to every comment and message personally",
          "Create case studies from your early wins",
          "Be transparent about your learning process",
        ],
        quickWins: [
          "Set up professional social media profiles",
          "Create one valuable lead magnet this week",
          "Join 5 relevant industry groups",
          "Post your first piece of helpful content",
          "Start collecting email addresses immediately",
        ],
        longTerm: [
          "Develop a signature methodology or framework",
          "Build a personal brand around your unique perspective",
          "Create a content calendar for consistent posting",
          "Launch your first paid offering within 90 days",
          "Establish yourself as the go-to person for your niche",
        ],
      },
      nextLevel: {
        name: "Emerging Authority",
        requirements: [
          "Grow to 1,000+ total followers",
          "Get your first 10 paying customers",
          "Generate ₦100,000+ monthly revenue",
          "Build an email list of 500+ subscribers",
        ],
      },
    },
    emerging: {
      score: 30,
      level: "Emerging Authority",
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      iconColor: "text-orange-500",
      description:
        "You're building momentum! People are starting to notice your expertise.",
      visibilityScore: 15,
      trustScore: 15,
      potential:
        "You're gaining traction! Focus on consistency and deeper relationships.",
      icon: <TrendingUp className="w-10 h-10" />,
      recommendations: {
        visibility: [
          "Start a weekly live video or podcast",
          "Guest post on popular blogs in your industry",
          "Speak at local events or webinars",
          "Create viral-worthy content (tips, behind-the-scenes)",
          "Cross-promote with other emerging authorities",
        ],
        trust: [
          "Share detailed case studies with results",
          "Create a signature program or course",
          "Collect and showcase client testimonials",
          "Offer free consultation calls to build relationships",
          "Host free workshops or masterclasses",
        ],
        quickWins: [
          "Launch a weekly newsletter with valuable insights",
          "Create 3 detailed case studies this month",
          "Host your first free webinar",
          "Optimize your social media for conversions",
          "Set up automated email sequences",
        ],
        longTerm: [
          "Build a community around your brand",
          "Develop multiple revenue streams",
          "Create a signature course or program",
          "Establish partnerships with bigger names",
          "Start speaking at industry conferences",
        ],
      },
      nextLevel: {
        name: "Building Authority",
        requirements: [
          "Reach 10,000+ total audience",
          "Convert 100+ paying customers",
          "Generate ₦500,000+ monthly revenue",
          "Build systems for consistent content creation",
        ],
      },
    },
    building: {
      score: 50,
      level: "Building Authority",
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
      iconColor: "text-yellow-500",
      description:
        "You have solid visibility but need to improve trust and conversions.",
      visibilityScore: 25,
      trustScore: 25,
      potential:
        "Huge conversion potential! Your audience is there, now monetize better.",
      icon: <Target className="w-10 h-10" />,
      recommendations: {
        visibility: [
          "Launch a YouTube channel or podcast",
          "Write a book or comprehensive guide",
          "Speak at major industry conferences",
          "Get featured in major publications",
          "Start a movement or challenge in your industry",
        ],
        trust: [
          "Develop a high-ticket signature program",
          "Create a certification or training program",
          "Build a mastermind or coaching group",
          "Implement advanced email nurture sequences",
          "Use retargeting ads to convert warm traffic",
        ],
        quickWins: [
          "Audit your sales funnel for conversion leaks",
          "Create a high-value, mid-tier offer (₦50,000-₦200,000)",
          "Implement social proof throughout your funnel",
          "Launch a referral program",
          "Add urgency and scarcity to your offers",
        ],
        longTerm: [
          "Build a scalable online course business",
          "Create a certification program for others",
          "Develop a personal brand media company",
          "Launch a mastermind or high-level group coaching",
          "Write and publish a book to cement authority",
        ],
      },
      nextLevel: {
        name: "Rising Authority",
        requirements: [
          "Optimize conversion rates above 5%",
          "Scale to ₦2,000,000+ monthly revenue",
          "Build a team to support growth",
          "Develop premium offerings (₦500,000+)",
        ],
      },
    },
    rising: {
      score: 70,
      level: "Rising Authority",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      iconColor: "text-blue-500",
      description:
        "You have strong authority with excellent growth potential ahead.",
      visibilityScore: 30,
      trustScore: 40,
      potential:
        "You're perfectly positioned to 10x! Scale your systems and expand reach.",
      icon: <BarChart3 className="w-10 h-10" />,
      recommendations: {
        visibility: [
          "Launch a major industry report or study",
          "Start a media company or publication",
          "Host a large-scale conference or summit",
          "Get regular media appearances",
          "Build strategic partnerships with major brands",
        ],
        trust: [
          "Create a premium mastermind (₦1M+ per year)",
          "Launch a certification program for practitioners",
          "Develop a SaaS tool for your industry",
          "Offer done-for-you services",
          "Create exclusive, high-value experiences",
        ],
        quickWins: [
          "Launch a premium mastermind program",
          "Create a certification course",
          "Develop strategic partnerships",
          "Implement advanced marketing automation",
          "Build a world-class customer experience",
        ],
        longTerm: [
          "Build a multi-million naira business empire",
          "Become the definitive authority in your space",
          "Create multiple business divisions",
          "Mentor and develop other authorities",
          "Build a legacy brand that outlasts you",
        ],
      },
      nextLevel: {
        name: "Authority Expert",
        requirements: [
          "Reach 100,000+ engaged audience",
          "Generate ₦10,000,000+ monthly revenue",
          "Build a complete business ecosystem",
          "Become the go-to expert in your industry",
        ],
      },
    },
    expert: {
      score: 85,
      level: "Authority Expert",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      iconColor: "text-purple-500",
      description:
        "You are a recognized expert with exceptional authority and influence.",
      visibilityScore: 40,
      trustScore: 45,
      potential:
        "Focus on legacy building and expanding into new markets or industries.",
      icon: <Crown className="w-10 h-10" />,
      recommendations: {
        visibility: [
          "Launch a major industry conference or awards",
          "Start a venture capital or investment fund",
          "Write multiple bestselling books",
          "Get regular TV/major media appearances",
          "Build a personal brand media empire",
        ],
        trust: [
          "Create a billion-naira business ecosystem",
          "Launch multiple complementary businesses",
          "Develop franchise or licensing opportunities",
          "Create investment opportunities for followers",
          "Build generational wealth systems",
        ],
        quickWins: [
          "Diversify into complementary industries",
          "Launch an investment or venture fund",
          "Create a business acquisition strategy",
          "Develop international expansion plans",
          "Build a personal brand holding company",
        ],
        longTerm: [
          "Build a business empire across multiple industries",
          "Create a foundation or scholarship program",
          "Mentor the next generation of authorities",
          "Leave a lasting legacy in your industry",
          "Become a household name and cultural icon",
        ],
      },
      nextLevel: {
        name: "Industry Icon",
        requirements: [
          "You've reached the pinnacle!",
          "Focus on legacy and impact",
          "Mentor others to reach authority",
          "Build generational wealth and influence",
        ],
      },
    },
  };

  const currentResult = authorityResults[selectedResult];

  const submitLead = () => {
    if (leadData.name && leadData.email) {
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Thank You, {leadData.name}!
          </h2>
          <p className="text-gray-600 mb-6">
            Your personalized 10x Authority Action Plan has been sent to{" "}
            {leadData.email}!
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
          >
            View More Results
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Authority Quotient Results
          </h1>
          <p className="text-gray-600 text-lg">
            Explore different authority levels and their strategies
          </p>
        </div>

        {/* Level Selector */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Select Authority Level to Explore:
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(authorityResults).map(([key, result]) => (
              <button
                key={key}
                onClick={() => setSelectedResult(key)}
                className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                  selectedResult === key
                    ? `${result.bgColor} border-current ${result.color}`
                    : "border-gray-200 hover:border-gray-300 text-gray-700"
                }`}
              >
                <div className="flex flex-col items-center">
                  <div
                    className={`mb-2 ${
                      selectedResult === key
                        ? result.iconColor
                        : "text-gray-400"
                    }`}
                  >
                    {result.icon}
                  </div>
                  <div className="text-xs">{result.level}</div>
                  <div className="text-lg font-bold">{result.score}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main Result Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
          {/* Header Section */}
          <div className={`${currentResult.bgColor} p-8`}>
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="text-center md:text-left mb-6 md:mb-0">
                <div
                  className={`w-20 h-20 ${currentResult.bgColor} rounded-full flex items-center justify-center mx-auto md:mx-0 mb-4 border-4 border-white shadow-lg`}
                >
                  <div className={currentResult.iconColor}>
                    {currentResult.icon}
                  </div>
                </div>
                <div className="text-6xl font-bold mb-2 text-gray-800">
                  {currentResult.score}
                </div>
                <div
                  className={`text-2xl font-semibold ${currentResult.color} mb-2`}
                >
                  {currentResult.level}
                </div>
                <p className="text-gray-700 max-w-md">
                  {currentResult.description}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-blue-600">
                    {currentResult.visibilityScore}
                  </div>
                  <div className="text-sm text-gray-600">Visibility</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <Shield className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-green-600">
                    {currentResult.trustScore}
                  </div>
                  <div className="text-sm text-gray-600">Trust</div>
                </div>
              </div>
            </div>
          </div>

          {/* Potential Section */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start">
              <Zap className="w-6 h-6 text-yellow-500 mr-3 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">
                  Your Authority Potential
                </h3>
                <p className="text-gray-600">{currentResult.potential}</p>
              </div>
            </div>
          </div>

          {/* Recommendations Section */}
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Visibility Strategies */}
              <div>
                <div className="flex items-center mb-4">
                  <Users className="w-6 h-6 text-blue-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-800">
                    Boost Visibility
                  </h3>
                </div>
                <div className="space-y-3">
                  {currentResult.recommendations.visibility.map(
                    (item, index) => (
                      <div key={index} className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-blue-500 mr-2 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Trust Strategies */}
              <div>
                <div className="flex items-center mb-4">
                  <Heart className="w-6 h-6 text-red-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-800">
                    Build Trust
                  </h3>
                </div>
                <div className="space-y-3">
                  {currentResult.recommendations.trust.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <ArrowRight className="w-4 h-4 text-red-500 mr-2 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Action Plans */}
          <div className="p-6 bg-gray-50">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Quick Wins */}
              <div>
                <div className="flex items-center mb-4">
                  <Lightbulb className="w-6 h-6 text-yellow-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-800">
                    Quick Wins (Next 30 Days)
                  </h3>
                </div>
                <div className="space-y-3">
                  {currentResult.recommendations.quickWins.map(
                    (item, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Long-term Strategy */}
              <div>
                <div className="flex items-center mb-4">
                  <Target className="w-6 h-6 text-purple-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-800">
                    Long-term Strategy
                  </h3>
                </div>
                <div className="space-y-3">
                  {currentResult.recommendations.longTerm.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <ArrowRight className="w-4 h-4 text-purple-500 mr-2 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Next Level */}
          <div className="p-6 border-t border-gray-200">
            <div className="flex items-start">
              <Rocket className="w-6 h-6 text-green-600 mr-3 mt-1" />
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Path to {currentResult.nextLevel.name}
                </h3>
                <div className="space-y-2">
                  {currentResult.nextLevel.requirements.map((req, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm">{req}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lead Generation CTA */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl shadow-xl p-8 text-white">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">
              Ready to 10x Your Authority?
            </h2>
            <p className="text-purple-100 text-lg">
              Get your personalized action plan with step-by-step strategies,
              templates, and tools.
            </p>
          </div>

          <div className="max-w-md mx-auto space-y-4">
            <input
              type="text"
              placeholder="Your Name"
              value={leadData.name}
              onChange={(e) =>
                setLeadData((prev) => ({ ...prev, name: e.target.value }))
              }
              className="w-full px-4 py-3 rounded-lg text-gray-800 focus:ring-2 focus:ring-white focus:ring-opacity-50"
            />
            <input
              type="email"
              placeholder="Your Email Address"
              value={leadData.email}
              onChange={(e) =>
                setLeadData((prev) => ({ ...prev, email: e.target.value }))
              }
              className="w-full px-4 py-3 rounded-lg text-gray-800 focus:ring-2 focus:ring-white focus:ring-opacity-50"
            />
            <button
              onClick={submitLead}
              disabled={!leadData.name || !leadData.email}
              className="w-full bg-white text-purple-600 py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-semibold"
            >
              Get My Complete 10x Authority Blueprint
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </div>

          <div className="text-center mt-6">
            <p className="text-purple-100 text-sm">
              🎁 Includes: Strategy templates, content calendars, email
              sequences, and more!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorityQuotientResult;
